/**
 * 牛只市场
 * 品种 Tab + 搜索 + 筛选（防抖 300ms）+ 排序 + 列表/网格切换 + 分页
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { listCattle } from '@/api/cattle';
import type { Cattle, Paginated } from '@/types';
import { BREEDS } from '@/utils/constants';
import CattleCard from '@/components/common/CattleCard';
import FilterBar, { type FilterValue } from '@/components/common/FilterBar';
import Loading from '@/components/common/Loading';
import ErrorBox from '@/components/common/ErrorBox';
import Empty from '@/components/common/Empty';
import PageHero from '@/components/common/PageHero';
import '@/styles/pages/market.css';

const SORTS = [
  { value: 'newest', label: '最新发布' },
  { value: 'price_asc', label: '价格 ↑' },
  { value: 'price_desc', label: '价格 ↓' },
  { value: 'weight', label: '体重' },
  { value: 'distance', label: '距离' },
];

const PAGE_SIZE = 20;

/** 品类预置（交易大厅点击进入） */
function categoryPreset(category: string | null): Partial<FilterValue> {
  switch (category) {
    case '母牛':
      return { gender: 0 };
    case '育肥牛':
      return {};
    case '种牛':
      return {};
    case '犊牛':
      return {};
    default:
      return {};
  }
}

export default function Market() {
  const [searchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const category = searchParams.get('category');
  const breedParam = searchParams.get('breed');

  const [activeBreed, setActiveBreed] = useState(breedParam ?? '全部');
  const [keyword, setKeyword] = useState(q);
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [sort, setSort] = useState('newest');
  const [filters, setFilters] = useState<FilterValue>(() => categoryPreset(category));
  const [page, setPage] = useState(1);

  const [data, setData] = useState<Paginated<Cattle> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const debounceRef = useRef<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listCattle({
        page,
        page_size: PAGE_SIZE,
        q: keyword || undefined,
        breed: activeBreed === '全部' ? filters.breed : activeBreed,
        gender: filters.gender,
        min_weight: filters.min_weight,
        max_weight: filters.max_weight,
        min_price: filters.min_price,
        max_price: filters.max_price,
        region: filters.region,
        trade_mode: filters.trade_mode,
        quarantine: filters.quarantine,
        sort,
        category: category ?? undefined,
      });
      setData(result);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [page, activeBreed, keyword, sort, filters, category]);

  useEffect(() => {
    load();
  }, [load]);

  // 搜索防抖 300ms（US-TRD-01）
  const onKeywordChange = (value: string) => {
    setKeyword(value);
    if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => setPage(1), 300);
  };

  const hasFilter =
    filters.breed ||
    filters.min_weight ||
    filters.max_weight ||
    filters.min_price ||
    filters.max_price ||
    filters.region ||
    filters.trade_mode ||
    filters.quarantine;

  const totalPages = useMemo(
    () => (data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1),
    [data],
  );

  return (
    <div className="market-page">
      <PageHero eyebrow="TRADE MARKET" title="牛只市场" desc="品种多维筛选 · 实时行情参考 · 交易方式灵活">
        <Link className="btn btn-outline-light btn-sm" to="/quotes">📊 行情看板</Link>
      </PageHero>

      <div className="container">
        {/* 工具条 */}
        <div className="market-toolbar">
          <div className="market-tabs">
            {['全部', ...BREEDS].map((b) => (
              <button
                key={b}
                className={`tab-item ${activeBreed === b ? 'active' : ''}`}
                onClick={() => {
                  setActiveBreed(b);
                  setPage(1);
                }}
              >
                {b}
              </button>
            ))}
          </div>
          <div className="market-toolbar__ops">
            <input
              className="form-control market-search"
              placeholder="搜索品种 / 耳标号 / 养殖场"
              value={keyword}
              onChange={(e) => onKeywordChange(e.target.value)}
            />
            <select
              className="form-control market-sort"
              value={sort}
              onChange={(e) => {
                setSort(e.target.value);
                setPage(1);
              }}
            >
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <div className="market-view">
              <button
                className={`market-view__btn ${view === 'grid' ? 'active' : ''}`}
                onClick={() => setView('grid')}
                aria-label="网格视图"
              >
                ▦
              </button>
              <button
                className={`market-view__btn ${view === 'list' ? 'active' : ''}`}
                onClick={() => setView('list')}
                aria-label="列表视图"
              >
                ☰
              </button>
            </div>
          </div>
        </div>

        <div className="market-body">
          <aside className="market-side">
            <FilterBar
              value={filters}
              onChange={(next) => {
                setFilters(next);
                setPage(1);
              }}
              onClear={() => {
                setFilters({});
                setPage(1);
              }}
            />
            <div className="market-side__links">
              <Link to="/orders" className="market-side__link">📋 我的订单</Link>
              <Link to="/auctions" className="market-side__link">🔨 竞价大厅</Link>
            </div>
          </aside>

          <section className="market-content">
            {loading ? (
              <Loading rows={6} type={view === 'grid' ? 'card' : 'list'} />
            ) : error ? (
              <ErrorBox message={error} onRetry={load} />
            ) : !data || data.list.length === 0 ? (
              <Empty
                icon="🔍"
                title="未找到符合条件的牛只"
                desc="试试调整筛选条件或清除筛选"
                action={
                  hasFilter ? (
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        setFilters({});
                        setPage(1);
                      }}
                    >
                      清除筛选
                    </button>
                  ) : undefined
                }
              />
            ) : (
              <>
                <div className="market-count">
                  共 <b>{data.total}</b> 头牛只符合条件
                </div>
                <div className={view === 'grid' ? 'market-grid' : 'market-list'}>
                  {data.list.map((c) => (
                    <CattleCard cattle={c} key={c.id} mode={view === 'grid' ? 'grid' : 'list'} />
                  ))}
                </div>
                {totalPages > 1 ? (
                  <div className="pagination">
                    <button disabled={page <= 1} onClick={() => setPage(page - 1)}>上一页</button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                      .map((p, idx, arr) => (
                        <span key={p} style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                          {idx > 0 && p - arr[idx - 1] > 1 ? <span>…</span> : null}
                          <button className={p === page ? 'active' : ''} onClick={() => setPage(p)}>
                            {p}
                          </button>
                        </span>
                      ))}
                    <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>下一页</button>
                  </div>
                ) : null}
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
